/**
 * RouteCreationFAB Component
 * Floating Action Button for creating navigation routes with waypoints
 * 
 * Features:
 * - Camera capture for landmark photos
 * - Landmark type selection
 * - Step-by-step route building
 * - Final destination with API card capture
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Badge,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import FlagIcon from '@mui/icons-material/Flag';
import RouteIcon from '@mui/icons-material/Route';
import SaveIcon from '@mui/icons-material/Save';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { telegram } from '@services/telegram';
import { getCyberColors } from '../theme/cyberColors';
import { requestPreciseLocation } from '../services/highPrecisionLocation';
import { useAutoDismiss } from '../hooks/useAutoDismiss';
import { Well } from '../types';

// Landmark types with icons
const LANDMARK_TYPES = [
  { id: 'cattle_guard', label: 'Cattle Guard', icon: '🐄', color: '#f59e0b' },
  { id: 'turn_left', label: 'Turn Left', icon: '↩️', color: '#3b82f6' },
  { id: 'turn_right', label: 'Turn Right', icon: '↪️', color: '#3b82f6' },
  { id: 'gate', label: 'Gate', icon: '🚧', color: '#ef4444' },
  { id: 'intersection', label: 'Intersection', icon: '✖️', color: '#8b5cf6' },
  { id: 'road_fork', label: 'Road Fork', icon: '🔀', color: '#6366f1' },
  { id: 'bridge', label: 'Bridge', icon: '🌉', color: '#06b6d4' },
  { id: 'sign', label: 'Sign', icon: '🪧', color: '#10b981' },
  { id: 'tank_battery', label: 'Tank Battery', icon: '🛢️', color: '#f97316' },
  { id: 'pump_jack', label: 'Pump Jack', icon: '⛽', color: '#84cc16' },
  { id: 'brine_tank', label: 'Brine Tank', icon: '💧', color: '#0ea5e9' },
  { id: 'storage_yard', label: 'Storage Yard', icon: '📦', color: '#a855f7' },
  { id: 'office', label: 'Office/Building', icon: '🏢', color: '#64748b' },
  { id: 'other', label: 'Other', icon: '❓', color: '#94a3b8' },
  { id: 'destination', label: 'Destination (API Card)', icon: '🏁', color: '#22c55e' },
];

export interface RouteWaypoint {
  id?: string;
  sequenceNumber: number;
  isDestination: boolean;
  latitude: number;
  longitude: number;
  landmarkType: string;
  customLandmarkName?: string;
  description?: string;
  photoUrl?: string;
  photoFile?: File;
  photoPreview?: string;
  apiNumber?: string;
  facilityName?: string;
  facilityType?: string;
}

interface RouteCreationFABProps {
  mapCenter: { lat: number; lng: number } | null;
  onRouteCreated?: (routeId: string) => void;
  darkMode?: boolean;
  highContrast?: boolean;
  facilities?: Well[];  // Wells/facilities to detect nearby destinations
}

// Distance in feet between two lat/lng points
const calculateDistanceFeet = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 20902231; // Earth's radius in feet
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Find the nearest well within threshold (200 feet)
const findNearbyWell = (lat: number, lng: number, facilities: Well[], thresholdFeet: number = 200): Well | null => {
  let nearest: Well | null = null;
  let nearestDistance = Infinity;
  
  for (const facility of facilities) {
    const distance = calculateDistanceFeet(lat, lng, facility.latitude, facility.longitude);
    if (distance <= thresholdFeet && distance < nearestDistance) {
      nearest = facility;
      nearestDistance = distance;
    }
  }
  
  return nearest;
};

export function RouteCreationFAB({ 
  mapCenter, 
  onRouteCreated,
  darkMode = true,
  highContrast = false,
  facilities = []
}: RouteCreationFABProps) {
  const cyber = getCyberColors(darkMode, highContrast);

  // State
  const [isCreating, setIsCreating] = useState(false);
  const [waypoints, setWaypoints] = useState<RouteWaypoint[]>([]);
  const [showCamera, setShowCamera] = useState(false);
  const [showLandmarkPicker, setShowLandmarkPicker] = useState(false);
  const [showFinishDialog, setShowFinishDialog] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<{ file: File; preview: string } | null>(null);
  const [selectedLandmark, setSelectedLandmark] = useState<typeof LANDMARK_TYPES[0] | null>(null);
  const [waypointDescription, setWaypointDescription] = useState('');
  const [customLandmarkName, setCustomLandmarkName] = useState('');
  const [routeName, setRouteName] = useState('');
  const [routeDescription, setRouteDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [detectedWell, setDetectedWell] = useState<Well | null>(null);  // Auto-detected destination well

  useAutoDismiss(error, () => setError(null));

  // Refs
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Get telegram user info
  const getTelegramUser = () => ({
    userId: telegram.getUserId() || '',
    username: telegram.getUserName() || ''
  });

  // Get current location when creating
  useEffect(() => {
    if (!isCreating) {
      return;
    }

    let cancelled = false;

    const capturePreciseLocation = async () => {
      try {
        const result = await requestPreciseLocation({
          desiredAccuracyMeters: 15,
          refinementTimeoutMs: 6000,
          allowLastKnownFallback: true,
        });

        if (!cancelled && result.fix) {
          setCurrentLocation({ lat: result.fix.lat, lng: result.fix.lng });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Route creation location error:', error);
        }
      }
    };

    capturePreciseLocation();

    return () => {
      cancelled = true;
    };
  }, [isCreating]);

  // Handle photo capture
  const handlePhotoCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a JPEG, PNG, or WebP image');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setCapturedPhoto({
        file,
        preview: reader.result as string
      });
      setShowCamera(false);
      setShowLandmarkPicker(true);
    };
    reader.readAsDataURL(file);

    (async () => {
      try {
        const result = await requestPreciseLocation({
          desiredAccuracyMeters: 15,
          refinementTimeoutMs: 6000,
          allowLastKnownFallback: true,
        });

        if (result.fix) {
          setCurrentLocation({ lat: result.fix.lat, lng: result.fix.lng });
        } else if (result.reason) {
          console.warn('Unable to refresh location for waypoint:', result.reason);
        }
      } catch (error) {
        console.error('Precise waypoint location error:', error);
      }
    })();

    telegram.hapticFeedback('medium');
  };

  // Add waypoint
  const addWaypoint = () => {
    if (!capturedPhoto || !selectedLandmark) return;

    const location = currentLocation || mapCenter || { lat: 0, lng: 0 };

    const newWaypoint: RouteWaypoint = {
      sequenceNumber: waypoints.length + 1,
      isDestination: selectedLandmark.id === 'destination',
      latitude: location.lat,
      longitude: location.lng,
      landmarkType: selectedLandmark.id,
      customLandmarkName: selectedLandmark.id === 'other' ? customLandmarkName : undefined,
      description: waypointDescription || undefined,
      photoFile: capturedPhoto.file,
      photoPreview: capturedPhoto.preview,
    };

    setWaypoints([...waypoints, newWaypoint]);
    
    // Reset state
    setCapturedPhoto(null);
    setSelectedLandmark(null);
    setWaypointDescription('');
    setCustomLandmarkName('');
    setShowLandmarkPicker(false);
    
    telegram.hapticFeedback('success');

    // If destination, check for nearby well and auto-name route
    if (selectedLandmark.id === 'destination') {
      // Try to find a well within 200 feet
      const nearbyWell = findNearbyWell(location.lat, location.lng, facilities);
      if (nearbyWell) {
        setDetectedWell(nearbyWell);
        // Auto-set route name as WellName-API#
        const autoName = `${nearbyWell.wellName}-${nearbyWell.api}`;
        setRouteName(autoName);
        console.log(`[RouteCreation] 🎯 Auto-detected destination: ${autoName}`);
        telegram.hapticFeedback('success');
      } else {
        setDetectedWell(null);
        console.log('[RouteCreation] ⚠️ No well found within 200 feet of destination');
      }
      setShowFinishDialog(true);
    }
  };

  // Remove waypoint
  const removeWaypoint = (index: number) => {
    const newWaypoints = waypoints.filter((_, i) => i !== index);
    // Resequence
    newWaypoints.forEach((wp, i) => {
      wp.sequenceNumber = i + 1;
    });
    setWaypoints(newWaypoints);
    telegram.hapticFeedback('light');
  };

  // Save route
  const saveRoute = async () => {
    if (waypoints.length === 0) {
      setError('Add at least one waypoint');
      return;
    }

    // Route name is required - should be auto-set from detected well
    if (!routeName.trim()) {
      setError('No destination well detected within 200 feet. Place finish flag closer to a well marker.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const { userId, username } = getTelegramUser();
      
      // Create route first
      const routeId = `route_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Build coordinates from waypoints
      const coordinates = waypoints.map(wp => [wp.latitude, wp.longitude]);
      
      // Find destination waypoint
      const destination = waypoints.find(wp => wp.isDestination);

      // Create route record - use detected well info for destination
      const routeResponse = await fetch('/api/routes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-User-Id': userId,
          'X-Telegram-Username': username,
        },
        body: JSON.stringify({
          id: routeId,
          name: routeName,
          description: routeDescription || undefined,
          coordinates: JSON.stringify(coordinates),
          has_waypoints: 1,
          waypoint_count: waypoints.length,
          destination_name: detectedWell?.wellName || destination?.facilityName || routeName,
          destination_api_number: detectedWell?.api || destination?.apiNumber,
        })
      });

      if (!routeResponse.ok) {
        throw new Error('Failed to create route');
      }

      // Upload each waypoint with photo
      for (const waypoint of waypoints) {
        if (!waypoint.photoFile) continue;

        const formData = new FormData();
        formData.append('photo', waypoint.photoFile);
        formData.append('routeId', routeId);
        formData.append('sequenceNumber', waypoint.sequenceNumber.toString());
        formData.append('isDestination', waypoint.isDestination.toString());
        formData.append('latitude', waypoint.latitude.toString());
        formData.append('longitude', waypoint.longitude.toString());
        formData.append('landmarkType', waypoint.landmarkType);
        if (waypoint.customLandmarkName) {
          formData.append('customLandmarkName', waypoint.customLandmarkName);
        }
        if (waypoint.description) {
          formData.append('description', waypoint.description);
        }
        if (waypoint.apiNumber) {
          formData.append('apiNumber', waypoint.apiNumber);
        }
        if (waypoint.facilityName) {
          formData.append('facilityName', waypoint.facilityName);
        }
        if (waypoint.facilityType) {
          formData.append('facilityType', waypoint.facilityType);
        }

        const waypointResponse = await fetch('/api/route-waypoints', {
          method: 'POST',
          headers: {
            'X-Telegram-User-Id': userId,
            'X-Telegram-Username': username,
          },
          body: formData
        });

        if (!waypointResponse.ok) {
          console.error('Failed to upload waypoint:', await waypointResponse.text());
        }
      }

      telegram.hapticFeedback('success');
      telegram.showAlert(`✅ Route "${routeName}" created with ${waypoints.length} waypoints!`);
      
      // Reset everything
      setWaypoints([]);
      setRouteName('');
      setRouteDescription('');
      setDetectedWell(null);
      setIsCreating(false);
      setShowFinishDialog(false);
      
      onRouteCreated?.(routeId);
    } catch (err) {
      console.error('Save route error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save route');
      telegram.hapticFeedback('error');
    }

    setSaving(false);
  };

  // Cancel route creation
  const cancelCreation = () => {
    if (waypoints.length > 0) {
      telegram.showConfirm('Discard this route?', (confirmed) => {
        if (confirmed) {
          setWaypoints([]);
          setIsCreating(false);
          setCapturedPhoto(null);
          setSelectedLandmark(null);
        }
      });
    } else {
      setIsCreating(false);
    }
  };

  // Get landmark info by ID
  const getLandmark = (id: string) => LANDMARK_TYPES.find(l => l.id === id);

  return (
    <>
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handlePhotoCapture}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handlePhotoCapture}
      />

      {/* Main FAB - Hidden since route creation is in MapFabManager toolbar */}
      {/* Keeping the component for its dialogs/modals but hiding the floating button */}
      {false && !isCreating && (
        <Fab
          onClick={() => {
            setIsCreating(true);
            telegram.hapticFeedback('medium');
          }}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            width: 56,
            height: 56,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: `3px solid ${cyber.neonBlue}`,
            color: cyber.neonBlue,
            boxShadow: `0 0 20px ${cyber.neonBlue}40, 0 0 40px ${cyber.neonBlue}20`,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(15, 23, 42, 1)',
              boxShadow: `0 0 30px ${cyber.neonBlue}60, 0 0 60px ${cyber.neonBlue}30`,
              transform: 'scale(1.05)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <RouteIcon sx={{ fontSize: 28 }} />
        </Fab>
      )}

      {/* Creating Mode FABs */}
      {isCreating && (
        <>
          {/* Waypoint count badge */}
          <Badge
            badgeContent={waypoints.length}
            color="primary"
            sx={{
              position: 'absolute',
              bottom: 180,
              right: 24,
              '& .MuiBadge-badge': {
                background: cyber.neonGreen,
                color: cyber.bgPrimary,
                fontWeight: 700,
                fontSize: '1rem',
                minWidth: 28,
                height: 28,
              }
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: cyber.gradientCard,
                border: `2px solid ${cyber.neonBlue}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FlagIcon sx={{ color: cyber.neonBlue }} />
            </Box>
          </Badge>

          {/* Add Waypoint FAB */}
          <Fab
            onClick={() => {
              setShowCamera(true);
              telegram.hapticFeedback('light');
            }}
            sx={{
              position: 'absolute',
              bottom: 100,
              right: 16,
              width: 72,
              height: 72,
              background: `linear-gradient(135deg, ${cyber.neonGreen} 0%, ${cyber.neonCyan} 100%)`,
              border: `3px solid white`,
              boxShadow: `0 0 25px ${cyber.neonGreen}80`,
              '&:hover': {
                background: `linear-gradient(135deg, ${cyber.neonCyan} 0%, ${cyber.neonGreen} 100%)`,
              }
            }}
          >
            <CameraAltIcon sx={{ fontSize: 36, color: cyber.bgPrimary }} />
          </Fab>

          {/* Finish Route FAB */}
          {waypoints.length > 0 && (
            <Fab
              onClick={() => setShowFinishDialog(true)}
              size="medium"
              sx={{
                position: 'absolute',
                bottom: 100,
                right: 100,
                background: cyber.neonPurple,
                border: `2px solid ${cyber.neonCyan}`,
                '&:hover': {
                  background: cyber.neonBlue,
                }
              }}
            >
              <CheckIcon sx={{ color: 'white' }} />
            </Fab>
          )}

          {/* Cancel FAB */}
          <Fab
            onClick={cancelCreation}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 100,
              left: 16,
              background: cyber.neonRed + '40',
              border: `2px solid ${cyber.neonRed}`,
              '&:hover': {
                background: cyber.neonRed,
              }
            }}
          >
            <CloseIcon sx={{ color: 'white' }} />
          </Fab>

          {/* Current route preview strip */}
          {waypoints.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 190,
                left: 16,
                right: 16,
                maxHeight: 120,
                overflowX: 'auto',
                display: 'flex',
                gap: 1,
                py: 1,
                px: 1,
                background: cyber.bgCard + 'E0',
                borderRadius: 2,
                border: `1px solid ${cyber.borderDefault}`,
                backdropFilter: 'blur(10px)',
              }}
            >
              {waypoints.map((wp, index) => {
                const landmark = getLandmark(wp.landmarkType);
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 70,
                      p: 1,
                      borderRadius: 1,
                      background: wp.isDestination ? cyber.neonGreen + '20' : cyber.bgSecondary,
                      border: `1px solid ${wp.isDestination ? cyber.neonGreen : cyber.borderDefault}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '1.5rem' }}>
                      {wp.isDestination ? '🏁' : landmark?.icon || '📍'}
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '0.6rem', 
                      color: cyber.textSecondary,
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}>
                      {wp.sequenceNumber}. {landmark?.label?.split(' ')[0] || 'Point'}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeWaypoint(index)}
                      sx={{ 
                        mt: 0.5, 
                        p: 0.25,
                        color: cyber.neonRed,
                        '&:hover': { background: cyber.neonRed + '30' }
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}

      {/* Camera Source Dialog */}
      <Dialog
        open={showCamera}
        onClose={() => setShowCamera(false)}
        PaperProps={{
          sx: {
            background: cyber.gradientCard,
            border: `1px solid ${cyber.borderDefault}`,
            borderRadius: 3,
            minWidth: 300,
          }
        }}
      >
        <DialogTitle sx={{ color: cyber.textPrimary, textAlign: 'center' }}>
          📸 Add Waypoint Photo
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<CameraAltIcon />}
            onClick={() => cameraInputRef.current?.click()}
            sx={{
              py: 2,
              background: `linear-gradient(135deg, ${cyber.neonGreen} 0%, ${cyber.neonCyan} 100%)`,
              color: cyber.bgPrimary,
              fontWeight: 700,
              fontSize: '1.1rem',
            }}
          >
            Take Photo
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<PhotoLibraryIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              py: 2,
              borderColor: cyber.neonBlue,
              color: cyber.neonBlue,
              fontWeight: 600,
            }}
          >
            Choose from Gallery
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCamera(false)} sx={{ color: cyber.textMuted }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Landmark Type Picker Dialog */}
      <Dialog
        open={showLandmarkPicker}
        onClose={() => {
          setShowLandmarkPicker(false);
          setCapturedPhoto(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: cyber.gradientCard,
            border: `1px solid ${cyber.borderDefault}`,
            borderRadius: 3,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ color: cyber.textPrimary }}>
          Select Landmark Type
        </DialogTitle>
        <DialogContent>
          {/* Photo Preview */}
          {capturedPhoto && (
            <Box
              sx={{
                width: '100%',
                height: 200,
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2,
                border: `2px solid ${cyber.neonBlue}`,
              }}
            >
              <img
                src={capturedPhoto.preview}
                alt="Captured"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
          )}

          {/* Landmark Type Grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 1.5,
            mb: 2,
          }}>
            {LANDMARK_TYPES.map((landmark) => (
              <Box
                key={landmark.id}
                onClick={() => setSelectedLandmark(landmark)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 1.5,
                  borderRadius: 2,
                  cursor: 'pointer',
                  background: selectedLandmark?.id === landmark.id 
                    ? `${landmark.color}30`
                    : cyber.bgSecondary,
                  border: `2px solid ${selectedLandmark?.id === landmark.id ? landmark.color : 'transparent'}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: `${landmark.color}20`,
                    transform: 'scale(1.02)',
                  }
                }}
              >
                <Typography sx={{ fontSize: '1.8rem', mb: 0.5 }}>
                  {landmark.icon}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.7rem', 
                  color: cyber.textSecondary,
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}>
                  {landmark.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Custom name for "Other" */}
          {selectedLandmark?.id === 'other' && (
            <TextField
              fullWidth
              label="Landmark Name"
              value={customLandmarkName}
              onChange={(e) => setCustomLandmarkName(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="e.g., Big Tree, Red Barn"
            />
          )}

          {/* Description (optional) */}
          <TextField
            fullWidth
            label="Description (optional)"
            value={waypointDescription}
            onChange={(e) => setWaypointDescription(e.target.value)}
            multiline
            rows={2}
            placeholder="e.g., Turn left after the cattle guard"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: cyber.textPrimary,
                '& fieldset': { borderColor: cyber.borderDefault },
              },
              '& .MuiInputLabel-root': { color: cyber.textMuted },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => {
              setShowLandmarkPicker(false);
              setCapturedPhoto(null);
            }}
            sx={{ color: cyber.textMuted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedLandmark || (selectedLandmark.id === 'other' && !customLandmarkName)}
            onClick={addWaypoint}
            startIcon={<AddIcon />}
            sx={{
              background: `linear-gradient(135deg, ${cyber.neonGreen} 0%, ${cyber.neonCyan} 100%)`,
              color: cyber.bgPrimary,
              fontWeight: 700,
            }}
          >
            Add Waypoint
          </Button>
        </DialogActions>
      </Dialog>

      {/* Finish Route Dialog */}
      <Dialog
        open={showFinishDialog}
        onClose={() => setShowFinishDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: cyber.gradientCard,
            border: `1px solid ${cyber.neonGreen}`,
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: cyber.neonGreen, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon /> Finish Route
        </DialogTitle>
        <DialogContent>
          {/* Route Summary */}
          <Box sx={{ mb: 3, p: 2, background: cyber.bgSecondary, borderRadius: 2 }}>
            <Typography sx={{ color: cyber.textMuted, fontSize: '0.85rem', mb: 1 }}>
              Route Summary
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <Typography sx={{ color: cyber.neonBlue, fontSize: '2rem', fontWeight: 700 }}>
                  {waypoints.length}
                </Typography>
                <Typography sx={{ color: cyber.textSecondary, fontSize: '0.8rem' }}>
                  Waypoints
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: cyber.neonGreen, fontSize: '2rem', fontWeight: 700 }}>
                  {waypoints.filter(w => w.isDestination).length > 0 ? '✓' : '–'}
                </Typography>
                <Typography sx={{ color: cyber.textSecondary, fontSize: '0.8rem' }}>
                  Destination Set
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Waypoints list */}
          <Typography sx={{ color: cyber.textMuted, fontSize: '0.85rem', mb: 1 }}>
            Waypoints
          </Typography>
          <List dense sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
            {waypoints.map((wp, index) => {
              const landmark = getLandmark(wp.landmarkType);
              return (
                <ListItem key={index} sx={{ background: cyber.bgSecondary, borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Typography sx={{ fontSize: '1.4rem' }}>
                      {wp.isDestination ? '🏁' : landmark?.icon}
                    </Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ color: cyber.textPrimary, fontSize: '0.9rem' }}>
                        {wp.sequenceNumber}. {wp.customLandmarkName || landmark?.label}
                      </Typography>
                    }
                    secondary={wp.description}
                  />
                </ListItem>
              );
            })}
          </List>

          {/* Detected Destination Well */}
          {detectedWell ? (
            <Box sx={{ 
              p: 2, 
              mb: 2, 
              borderRadius: 2, 
              background: `linear-gradient(135deg, ${cyber.neonGreen}15 0%, ${cyber.neonCyan}20 100%)`,
              border: `1px solid ${cyber.neonGreen}40`
            }}>
              <Typography sx={{ color: cyber.neonGreen, fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>
                🎯 Destination Detected
              </Typography>
              <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '1.1rem' }}>
                {detectedWell.wellName}
              </Typography>
              <Typography sx={{ color: cyber.textSecondary, fontSize: '0.9rem' }}>
                API: {detectedWell.api}
              </Typography>
              <Typography sx={{ color: cyber.textMuted, fontSize: '0.8rem', mt: 0.5 }}>
                Route will be named: <strong>{routeName}</strong>
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.9rem' }}>
                ⚠️ No well found within 200 feet of your finish flag.
                Move closer to a well marker to auto-name the route.
              </Typography>
            </Alert>
          )}

          {/* Route Description */}
          <TextField
            fullWidth
            label="Description (optional)"
            value={routeDescription}
            onChange={(e) => setRouteDescription(e.target.value)}
            multiline
            rows={2}
            placeholder="Any additional notes about this route..."
            sx={{
              '& .MuiOutlinedInput-root': {
                color: cyber.textPrimary,
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setShowFinishDialog(false)}
            sx={{ color: cyber.textMuted }}
          >
            Continue Adding
          </Button>
          <Button
            variant="contained"
            disabled={saving || !detectedWell || waypoints.length === 0}
            onClick={saveRoute}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              background: detectedWell 
                ? `linear-gradient(135deg, ${cyber.neonGreen} 0%, ${cyber.neonCyan} 100%)`
                : cyber.bgSecondary,
              color: detectedWell ? cyber.bgPrimary : cyber.textMuted,
              fontWeight: 700,
              minWidth: 140,
            }}
          >
            {saving ? 'Saving...' : detectedWell ? 'Save Route' : 'Move Closer to Well'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
